import { NgModule, ModuleWithProviders, InjectionToken, Injector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { NotificationsModule } from '@content-platform/web-angular-shared-ui-notifications';
import { LoggingModule } from '@content-platform/web-angular-shared-util-logging';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { fromError } from './reducers';
import { ErrorEffects } from './effects/error.effects';
import { ErrorContainerComponent } from './error-container/error-container.component';
import { ErrorItemComponent } from './error-item/error-item.component';
import { GenericErrorGuard } from './guards/generic-error.guard';
import { WebAngularSharedUtilErrorHandlingConfig, WebAngularSharedUtilErrorHandlingOptions } from './error-handling-config';
import { ErrorDispatchService } from './error-dispatch.service';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { AccessDeniedComponent } from './access-denied/access-denied.component';
import { DevelopmentModule } from '@content-platform/web-angular-shared-util-development';

import { ReportParamsTransformer, REPORT_PARAM_TRANSFORMERS } from './models';
import { IssueReportingService } from './issue-reporting.service';
import { AcquireTokenErrorComponent } from './acquire-token-error/acquire-token-error.component';
import { MatDividerModule } from '@angular/material/divider';
import { MaintenancePageComponent } from './maintenance/maintenance-page.component';
/**
 * This factory allows me to initiate the transformer classes, and provides around this issue:
 *   Function expressions are not supported in decorators
 * This is why I am passing in the un initiated classes through the Injection token below.
 * The factory is only provided in the forRoot, so that would have be called in the main app.
 */
export const INITIAL_REPORT_TRANSFORMERS_CLASS = new InjectionToken(
  'initial-report-transformer-class'
);

export function classFactory(injector, paramTransformers) {
  return paramTransformers
    .filter((paramTransformer) => !!paramTransformer)
    .map((paramTransformerClass) => {
      return new paramTransformerClass(injector);
    });
}

const MATERIAL_MODULES = [
  MatCardModule,
  MatExpansionModule,
  MatIconModule,
  MatButtonModule,
  MatDividerModule,
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    StoreModule.forFeature('error', fromError.reducer),
    EffectsModule.forFeature([ErrorEffects]),
    NotificationsModule,
    DevelopmentModule,
    LoggingModule,
    ...MATERIAL_MODULES,
  ],
  declarations: [
    ErrorContainerComponent,
    ErrorItemComponent,
    PageNotFoundComponent,
    AccessDeniedComponent,
    AcquireTokenErrorComponent,
    MaintenancePageComponent,
  ],
  exports: [ErrorContainerComponent, ErrorItemComponent, MaintenancePageComponent],
  entryComponents: [ErrorContainerComponent],
  providers: [
    GenericErrorGuard,
    { provide: WebAngularSharedUtilErrorHandlingConfig, useValue: {} },
    ErrorDispatchService,
    IssueReportingService,
  ],
})
export class WebAngularSharedUtilErrorHandlingModule {
  static forRoot(
    options: WebAngularSharedUtilErrorHandlingOptions = {},
    paramTransformer?: typeof ReportParamsTransformer
  ): ModuleWithProviders<WebAngularSharedUtilErrorHandlingModule> {
    return {
      ngModule: WebAngularSharedUtilErrorHandlingModule,
      providers: [
        { provide: WebAngularSharedUtilErrorHandlingConfig, useValue: options },
        { provide: INITIAL_REPORT_TRANSFORMERS_CLASS, multi: true, useValue: paramTransformer },
        {
          provide: REPORT_PARAM_TRANSFORMERS,
          useFactory: classFactory,
          deps: [Injector, INITIAL_REPORT_TRANSFORMERS_CLASS],
        },
      ],
    };
  }

  static withExtraParams(
    paramTransformer: typeof ReportParamsTransformer
  ): ModuleWithProviders<WebAngularSharedUtilErrorHandlingModule> {
    return {
      ngModule: WebAngularSharedUtilErrorHandlingModule,
      providers: [
        { provide: INITIAL_REPORT_TRANSFORMERS_CLASS, multi: true, useValue: paramTransformer },
      ],
    };
  }
}
